import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAdminDoctor,
  getDoctorSignature,
  putDoctorSignature,
  listDoctorArchivedDocuments,
  mapArchivedDocuments,
  unarchiveDoctorDocument,
  archiveDoctorDocumentRequest,
  uploadDoctorFile,
  deleteDoctorFile,
  createDoctor,
  updateDoctorRequest,
  listDoctorForms,
  mapAdminDoctor,
} from "api/doctors";
import { converterDataField } from "utils/common";
import { queryKeys } from "./keys";
import { CACHE } from "config/cachePolicy";
import { USER_ROLES } from "store/currentUser/constants";

export function useDoctorDetails(uuid, options = {}) {
  return useQuery({
    queryKey: queryKeys.doctors.detail(uuid),
    queryFn: () => fetchAdminDoctor(uuid),
    enabled: !!uuid,
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export async function fetchDoctorSignatureData(doctorId) {
  const response = await getDoctorSignature(doctorId);
  if (!response.data?.signature_download_link) {
    return null;
  }
  const blob = await fetch(response.data.signature_download_link).then((r) =>
    r.blob(),
  );
  const file = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
  return {
    link: response.data.signature,
    file,
  };
}

export function useDoctorSignature(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.doctors.signature(doctorId),
    queryFn: () => fetchDoctorSignatureData(doctorId),
    enabled: !!doctorId,
    staleTime: CACHE.CATALOG,
    ...options,
  });
}

export function useUpsertDoctorSignature(doctorId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file) => putDoctorSignature(doctorId, file),
    onSuccess: (_data, file) => {
      queryClient.setQueryData(queryKeys.doctors.signature(doctorId), {
        link: _data?.data?.signature,
        file,
      });
    },
  });
}

export function useDoctorArchivedDocuments(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.doctors.archived(doctorId),
    queryFn: async () => {
      const response = await listDoctorArchivedDocuments(doctorId);
      return mapArchivedDocuments(response.data);
    },
    enabled: !!doctorId,
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export async function unarchiveDoctorDocumentRequest(
  doctorId,
  fileId,
  swappedFile,
) {
  return unarchiveDoctorDocument(doctorId, fileId, !!swappedFile);
}

export async function archiveDoctorDocumentApi(fileId) {
  return archiveDoctorDocumentRequest(fileId);
}

function upsertUploadApplication(documents, payload) {
  const isDocumentExists = documents.find(
    (document) => document.fileType === payload.fileType,
  );
  if (isDocumentExists) {
    return documents.map((document) =>
      document.fileType === payload.fileType ? payload : document,
    );
  }
  return [...documents, payload];
}

export function updateDoctorDetailsInCache(queryClient, uuid, updater) {
  queryClient.setQueryData(queryKeys.doctors.detail(uuid), (prev) => {
    if (!prev) return prev;
    return typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
  });
}

export function upsertDoctorDocumentInCache(queryClient, uuid, document) {
  updateDoctorDetailsInCache(queryClient, uuid, (prev) => ({
    ...prev,
    uploadApplications: upsertUploadApplication(
      prev.uploadApplications,
      document,
    ),
  }));
}

export function deleteDoctorDocumentInCache(queryClient, uuid, fileType) {
  updateDoctorDetailsInCache(queryClient, uuid, (prev) => ({
    ...prev,
    uploadApplications: prev.uploadApplications.filter(
      (document) => document.fileType !== fileType,
    ),
  }));
}

export function swapDoctorDocumentInCache(queryClient, uuid, document) {
  updateDoctorDetailsInCache(queryClient, uuid, (prev) => ({
    ...prev,
    uploadApplications: prev.uploadApplications.map((item) =>
      item.fileType === document.fileType ? document : item,
    ),
  }));
}

export function updateDoctorArchivedInCache(queryClient, doctorId, updater) {
  queryClient.setQueryData(queryKeys.doctors.archived(doctorId), (prev = []) =>
    updater(prev),
  );
}

export async function uploadDoctorFileRequest(body) {
  const resp = await uploadDoctorFile(body);
  return converterDataField(resp?.data, true);
}

export const deleteDoctorFileRequest = (uuid) => deleteDoctorFile(uuid);

export const createDoctorRequest = (body) =>
  createDoctor(body).then((resp) => {
    if (resp?.data?.uuid) return resp.data;
    throw "";
  });

export const updateDoctorProfileRequest = (uuid, body, isAdmin) =>
  updateDoctorRequest(uuid, body, isAdmin).then((resp) => {
    if (!resp?.data?.uuid) throw resp;
    return mapAdminDoctor(resp.data);
  });

export function useCreateDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDoctorRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
    },
  });
}

export function useUpdateDoctorProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body, isAdmin }) =>
      updateDoctorProfileRequest(uuid, body, isAdmin),
    onSuccess: (_data, { uuid, isAdmin }) => {
      if (isAdmin) {
        queryClient.invalidateQueries({ queryKey: queryKeys.doctors.all });
        if (uuid) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.doctors.detail(uuid),
          });
        }
        return;
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.currentUser.me(USER_ROLES.DOCTOR),
      });
    },
  });
}

export const fetchDoctorFormLinks = () => listDoctorForms();
